import './styles.scss';

import { Button, Col, InputGroup, Row } from 'react-bootstrap';
import {
  DispatchSummaryCallVolumeEnum,
  DispatchSummaryChatVolumeEnum,
  DispatchSummaryDispatcherAssetCamerasWorkingEnum,
  DispatchSummaryDispatcherShiftEnum,
  DispatchSummaryDispatcherWasThereEightByEightIssueEnum,
  DispatchSummaryMarket,
} from '@/models/gen/graphql';
import { EnumDropdownField, getItemsFromEnum } from '@/components/EnumDropdown';
import { useMemo, useRef, useState } from 'react';

import { AirportDropdownField } from '@/components/AirportDropdown';
import { DispatcherDropdownField } from '@/components/UserDropdown';
import Form from '@/common/Form';
import { LoadingBlur } from '@/components/LoadingSpinner';
import { SummaryHistoryFormDefinitions } from '@/features/SummaryHistory/components/SummaryHistoryForm/types';
import { TextAreaField } from '@/components/TextArea';
import { Toast } from '@/models';
import { createDispatchSummary } from '@/features/SummaryHistory/api/services/createDispatchSummary';
import { createDispatchSummaryMarket } from '@/features/SummaryHistory/api/services/createDispatchSummaryMarket';
import { createNotification } from '@/utils';
import { deleteDispatchSummaryMarket } from '@/features/SummaryHistory/api/services/deleteDispatchSummaryMarket';
import { updateDispatchSummary } from '@/features/SummaryHistory/api/services/updateDispatchSummary';
import { updateDispatchSummaryMarket } from '@/features/SummaryHistory/api/services/updateDispatchSummaryMarket';
import useLocale from '@/hooks/useLocale';
import { useSummaryHistory } from '@/features/SummaryHistory';
import { useServiceIssueFilters } from '@/hooks/useServiceIssueFilters';

type MarketCommentsState = {
  deletedIds: string[];
};

const initMarketCommentsState: MarketCommentsState = {
  deletedIds: [],
};

const SummaryHistoryForm = ({ onSubmit, locale: customLocale, value = {} }: SummaryHistoryFormDefinitions.Props): JSX.Element => {
  const [, setState, { submitFilters }] = useSummaryHistory();
  const [marketCommentsState, setMarketCommentsState] = useState<MarketCommentsState>(initMarketCommentsState);
  const [loading, setLoading] = useState(false);
  const locale = useLocale({
    ...SummaryHistoryFormDefinitions.getLocale(value),
    ...customLocale,
  });
  const updateMarketComments = useRef<Record<string, string>>({});
  let createId = null;
  const handleSubmit = async (data) => {
    try {
      setLoading(true);
      const { marketCommentsForm, marketComments, ...rest } = data;
      // Check if we need to update or create any comments
      let updatedComments = [];
      let newComments = [];
      Object.keys(updateMarketComments?.current ?? {})?.map((comment) => {
        // If the comment airportcode exists in the marketComments, we need to update it
        const foundIndex = value.marketComments?.findIndex((marketComment: DispatchSummaryMarket) => marketComment.airportCode === comment);
        if (foundIndex >= 0) {
          const comment = value.marketComments?.[foundIndex];
          updatedComments = [
            ...updatedComments,
            {
              id: comment.id,
              airportCode: comment.airportCode,
              comment: updateMarketComments.current[comment.airportCode],
              dispatchSummaryId: data.id,
            },
          ];
        } else {
          // Otherwise we need to create it
          newComments = [
            ...newComments,
            {
              airportCode: comment,
              comment: updateMarketComments.current[comment],
              dispatchSummaryId: data.id || '',
            },
          ];
        }
      });
      if (rest.id) {
        await updateDispatchSummary([rest]);
        createNotification('Sucessfully Updated Dispatch Summary', Toast.Type.SUCCESS, 'Dispatch Summary');
        if (updatedComments.length > 0) {
          await updateDispatchSummaryMarket(updatedComments.filter((comment) => !marketCommentsState.deletedIds.includes(comment.id)));
        }
        if (newComments.length > 0) {
          await createDispatchSummaryMarket(newComments);
        }
        setState((current) => ({
          ...current,
          summaries: current.summaries.set(rest.id, {
            ...current.summaries.get(rest.id),
            ...rest,
          }),
        }));
      } else {
        const { id, ...create } = rest;
        if (newComments.length > 0) {
          create.marketComments = newComments;
        }
        const createRes = await createDispatchSummary([create]);
        createNotification('Sucessfully Created Dispatch Summary', Toast.Type.SUCCESS, 'Dispatch Summary');
        createId = createRes?.[0]?.node?.id;
      }
      // Check if we have any new comments to delete
      const deletedIds = marketCommentsState.deletedIds.filter((id) => id !== null);
      if (deletedIds.length > 0) {
        await deleteDispatchSummaryMarket(deletedIds.map((id) => ({ id })));
      }
    } catch (err) {
      console.error('Error Creating Dispatch Summary.', err);
      createNotification('Could Not Create Dispatch Summary', Toast.Type.DANGER, 'Dispatch Summary');
    } finally {
      await submitFilters?.(useServiceIssueFilters.getState().state);
      setLoading(false);
      onSubmit?.(data);
    }
  };
  const fullValue = useMemo(
    () => ({
      ...SummaryHistoryFormDefinitions.initValue,
      ...value,
      ...SummaryHistoryFormDefinitions.loadForm(),
    }),
    []
  );

  return (
    <Form
      name="SummaryHistoryForm"
      className="SummaryHistoryForm"
      onSubmit={handleSubmit}
      value={fullValue}
      validator={SummaryHistoryFormDefinitions.validator(locale)}
      onChange={SummaryHistoryFormDefinitions.saveForm}
    >
      <LoadingBlur loading={loading} />
      <Form.Input name="id" type="hidden" />
      <Row className="SummaryHistoryFormHeader">
        <Col>
          <h4 className="SummaryHistoryFormTitle">{locale('Dispatch Summary')}</h4>
        </Col>
      </Row>
      <Row>
        <Col>
          <Form.Input
            validate
            as={EnumDropdownField}
            name="callVolume"
            label={locale('Call Volume')}
            enum={getItemsFromEnum(DispatchSummaryCallVolumeEnum)}
            options={{
              locale: {
                Nominal: locale('Nominal - under 10 calls per hour'),
                Low: locale('Low - under 50 calls per hour'),
                Medium: locale('Medium - 50-100 calls per hour'),
                High: locale('High - over 100 calls per hour'),
              },
            }}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <Form.Input
            validate
            as={EnumDropdownField}
            name="chatVolume"
            label={locale('Chat Volume')}
            enum={getItemsFromEnum(DispatchSummaryChatVolumeEnum)}
            options={{
              locale: {
                Low: locale('Low - 20 chats per hour'),
                Medium: locale('Medium - 50 chats per hour'),
                High: locale('High - 75+ chats per hour'),
              },
            }}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={6}>
          <Form.Input as={DispatcherDropdownField} name="dispatcherId" label={locale('Dispatcher')} validate />
        </Col>
        <Col xs={6}>
          <Form.Input
            as={EnumDropdownField}
            name="dispatcherShift"
            label={locale('Your Shift')}
            enum={getItemsFromEnum(DispatchSummaryDispatcherShiftEnum)}
            validate
            options={{
              showClearButton: false,
            }}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <Form.Input as={TextAreaField} name="dispatcherDayOverview" label={locale('Overview Of The Day')} validate />
        </Col>
      </Row>
      <Row>
        <Col>
          <Form.Input as={TextAreaField} name="dispatcherPassdown" label={locale('Passdown Completed? By Whom?')} validate />
        </Col>
      </Row>
      <Row>
        <Col>
          <Form.Input
            as={EnumDropdownField}
            name="dispatcherWasThereEightByEightIssue"
            label={locale('Was There An 8X8 Issue Today?')}
            enum={DispatchSummaryDispatcherWasThereEightByEightIssueEnum}
            validate
            options={{
              showClearButton: false,
            }}
          />
        </Col>
      </Row>
      <Form.Watch name="dispatcherWasThereEightByEightIssue">
        {([wasThereEightByEightIssue]: [DispatchSummaryDispatcherWasThereEightByEightIssueEnum]) =>
          wasThereEightByEightIssue === DispatchSummaryDispatcherWasThereEightByEightIssueEnum.Yes && (
            <Row>
              <Col>
                <Form.Input
                  as={TextAreaField}
                  name="dispatcherEightByEightIssue"
                  label={locale('Was There An 8X8 Issue Today? Explain')}
                  validate
                />
              </Col>
            </Row>
          )
        }
      </Form.Watch>
      <Row>
        <Col>
          <Form.Input
            as={EnumDropdownField}
            name="dispatcherAssetCamerasWorking"
            label={locale('Were All Samsara Cameras Working In All Stations?')}
            enum={DispatchSummaryDispatcherAssetCamerasWorkingEnum}
            validate
            options={{
              showClearButton: false,
            }}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <Form.Input
            as={TextAreaField}
            name="dispatcherAssetCamerasWorkingNotes"
            label={locale('List Stations And Van Numbers Not Working')}
            validate
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <Form.Input
            as={TextAreaField}
            name="dispatcherSoftwareIssues"
            label={locale('Any Issues With New Software? Please Explain')}
            validate
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <Form.Input
            as={TextAreaField}
            name="commentsRepeatSpeedingOffenders"
            label={locale('Repeat speeding offenders: (Events 10MPH over limit)')}
            validate
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <Form.Input
            as={TextAreaField}
            name="commentsRepeatDistractedOffenders"
            label={locale('Repeat offenders For distracted driving:')}
            validate
          />
        </Col>
      </Row>
      <Row className="SummaryHistoryFormSectionHeader">
        <Col>
          <h4 className="SummaryHistoryFormCommentsTitle">{locale('Comments And Changes By City')}</h4>
        </Col>
      </Row>
      <Form.Watch name="marketCommentsForm">
        {([marketComments]: Record<string, string>[], form) => {
          // Update refs
          updateMarketComments.current = marketComments as Record<string, string>;
          const handleAddCity = (city: string) => {
            if (!city || marketComments?.[city]) return;
            form.setValue(`marketCommentsForm.${city}`, '', { shouldValidate: true });
          };
          const handleRemoveCity = (city) => {
            const id = value.marketComments?.find((comment) => comment.airportCode === city)?.id || null;
            const current = form.getValues(`marketCommentsForm`);
            delete current[city];
            form.setValue(`marketCommentsForm`, current || {}, { shouldValidate: true });
            setMarketCommentsState((current) => ({
              ...current,
              deletedIds: [...current.deletedIds, id],
            }));
            // Re-trigger validation since we're changing the value manually.
            form.trigger();
          };
          return (
            <>
              {Object.entries(marketComments ?? {})?.map(([key, value]) => {
                return (
                  <Row key={key}>
                    <Col>
                      <InputGroup>
                        <Form.Input
                          as={TextAreaField}
                          className="CommentsByCity"
                          value={value}
                          name={`marketCommentsForm.${key}`}
                          label={key}
                        />
                        <Button variant="icon" onClick={() => handleRemoveCity(key)}>
                          <i className="sv sv-trash2 text-danger" />
                        </Button>
                      </InputGroup>
                    </Col>
                  </Row>
                );
              })}
              <Row>
                <Col>
                  <AirportDropdownField value="" name="addCity" placeholder={locale('Add City')} onChange={handleAddCity} />
                </Col>
              </Row>
            </>
          );
        }}
      </Form.Watch>
      <Row>
        <Col>
          <Form.Input as={TextAreaField} name="manifestsTaxis" label={locale('Taxis/Lyft By Station')} validate />
        </Col>
      </Row>
      <Row>
        <Col>
          <Form.Input
            as={TextAreaField}
            name="manifestsTrainingIssues"
            label={locale('Training Issues By Class')}
            placeholder={locale('Detailed Explanation')}
            validate
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <Form.Input
            as={TextAreaField}
            name="manifestsDrivingIssues"
            label={locale('Driving Issues By Station')}
            placeholder={locale('Detailed Explanation')}
            validate
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <Row>
            <Col>
              <Form.Button name="SUBMIT" type="submit" variant={locale('SUBMIT_BUTTON_COLOR')}>
                {locale('SUBMIT_BUTTON_TEXT')}
              </Form.Button>
            </Col>
          </Row>
        </Col>
      </Row>
    </Form>
  );
};

export default SummaryHistoryForm;
